import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Copy,
  Trash2,
  Plus,
  Check,
  AlertCircle,
  Package,
  Calculator,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  getBundle,
  removeFromBundle,
  clearBundle,
  calculateBundlePrice,
  encodeBundleToQuery,
  decodeBundleFromQuery,
  mergeBundleFromQuery,
} from '@/lib/bundles';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { fireEvent } from '@/lib/analytics';

interface DomainData {
  domain: string;
  bin_price?: number;
  tags?: string[];
  bundle?: string;
}

export default function BundleBuilder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { toast } = useToast();

  const [bundleItems, setBundleItems] = useState<string[]>([]);
  const [domainsData, setDomainsData] = useState<DomainData[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    intent: '',
  });

  // Load bundle items and sync with URL
  useEffect(() => {
    const urlItems = searchParams.get('items');
    let items: string[] = [];

    if (urlItems) {
      const queryItems = decodeBundleFromQuery(urlItems);
      items = mergeBundleFromQuery(queryItems);
    } else {
      items = getBundle();
    }

    setBundleItems(items);

    // Analytics
    fireEvent('bundle_view_builder', {
      count: items.length,
      from_url: !!urlItems,
    });
  }, [searchParams]);

  // Update URL when bundle changes
  useEffect(() => {
    if (bundleItems.length > 0) {
      const queryString = encodeBundleToQuery(bundleItems);
      setSearchParams({ items: queryString }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [bundleItems, setSearchParams]);

  // Fetch domain data
  useEffect(() => {
    async function fetchDomainsData() {
      if (bundleItems.length === 0) {
        setDomainsData([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const { data, error } = await supabase
          .from('domains')
          .select('domain, bin_price, tags, bundle')
          .in('domain', bundleItems)
          .eq('owned', true);

        if (error) throw error;

        // Maintain order from bundleItems
        const orderedData = bundleItems
          .map((domain) => data?.find((d) => d.domain === domain))
          .filter(Boolean) as DomainData[];

        setDomainsData(orderedData);
      } catch (error) {
        console.error('Error fetching bundle domains:', error);
        toast({
          title: 'Error loading domains',
          description: 'Some domains may not be available.',
          variant: 'destructive',
        });
        setDomainsData([]);
      } finally {
        setLoading(false);
      }
    }

    fetchDomainsData();
  }, [bundleItems, toast]);

  const handleRemoveDomain = (domain: string) => {
    removeFromBundle(domain);
    setBundleItems((prev) => prev.filter((d) => d !== domain));
  };

  const handleClearAll = () => {
    if (bundleItems.length === 0) return;

    if (confirm(`Remove all ${bundleItems.length} domains from your bundle?`)) {
      clearBundle();
      setBundleItems([]);
      toast({
        title: 'Bundle cleared',
        description: 'All domains have been removed from your bundle.',
      });
    }
  };

  const handleCopyShareLink = async () => {
    const url = `${window.location.origin}/bundles/builder?items=${encodeBundleToQuery(bundleItems)}`;

    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: 'Link copied!',
        description: 'Share this link to let others see your bundle.',
      });
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      toast({
        title: 'Link copied!',
        description: 'Share this link to let others see your bundle.',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.intent) {
      toast({
        title: 'Missing information',
        description: "Please fill in your email and describe what you'll build.",
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const pricing = calculateBundlePrice(domainsData);
      const domains = bundleItems.join(', ');

      // Prepare payload
      const payload = {
        name: formData.name || 'Not provided',
        email: formData.email,
        company: '',
        domain: `Bundle: ${domains}`,
        kind: 'bundle',
        notes: formData.intent,
        src: new URLSearchParams(window.location.search).get('src') || '',
        host: new URLSearchParams(window.location.search).get('host') || '',
        // Bundle-specific fields
        bundle_domains: bundleItems,
        bundle_count: bundleItems.length,
        bundle_subtotal: pricing.subtotal,
        bundle_discount: pricing.discountAmount,
        bundle_price: pricing.bundlePrice,
        bundle_discount_label: pricing.discountLabel,
      };

      // Submit to intents table
      const { error: intentError } = await supabase.from('intents').insert([
        {
          name: payload.name,
          email: payload.email,
          company: payload.company,
          domain: payload.domain,
          kind: payload.kind,
          src: payload.src,
          host: payload.host,
          status: 'pending',
        },
      ]);

      if (intentError) throw intentError;

      // Analytics
      fireEvent('bundle_request_submit', {
        count: bundleItems.length,
        subtotal: pricing.subtotal,
        discount: pricing.discountAmount,
        bundlePrice: pricing.bundlePrice,
      });

      setSubmitted(true);
      toast({
        title: 'Bundle request submitted!',
        description: "I'll get back to you within 24 hours with pricing.",
      });
    } catch (error) {
      console.error('Error submitting bundle request:', error);
      toast({
        title: 'Submission failed',
        description: 'Please try again or contact me directly.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const pricing = calculateBundlePrice(domainsData);

  const seoTitle = `Bundle Builder - ${bundleItems.length} domains selected`;
  const seoDescription = `Build a custom domain bundle and get a discounted price. ${bundleItems.length} domains selected.`;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>{withTemplate(seoTitle)}</title>
          <meta name="description" content={seoDescription} />
        </Helmet>

        <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
          <div className="max-w-2xl mx-auto text-center py-20">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-4">Bundle Request Submitted!</h1>
            <p className="text-muted-foreground mb-8">
              Thank you for your interest in my domain bundles. I'll review your request and get
              back to you within 24 hours with custom pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/domains">Browse More Domains</Link>
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Create Another Bundle
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{withTemplate(seoTitle)}</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/domains"
              className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Domain Explorer
            </Link>

            <div className="flex items-center gap-3 mb-4">
              <Package className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Bundle Builder</h1>
            </div>

            <p className="text-muted-foreground">
              Build a custom package and get bulk pricing on multiple domains.
            </p>
          </div>

          {bundleItems.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Package className="w-12 h-12 text-muted-foreground/50" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Your bundle is empty</h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Add domains to your bundle from the Domain Explorer to see pricing and request a
                custom quote.
              </p>
              <Button asChild>
                <Link to="/domains">
                  <Plus className="w-4 h-4 mr-2" />
                  Browse Domains
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Selected Domains */}
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-foreground">
                    Selected Domains ({bundleItems.length})
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyShareLink}
                      disabled={bundleItems.length === 0}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Share Bundle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearAll}
                      disabled={bundleItems.length === 0}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="space-y-4">
                    {bundleItems.map((_, i) => (
                      <Card key={i} className="p-4">
                        <div className="animate-pulse flex items-center justify-between">
                          <div className="h-6 bg-muted rounded w-48"></div>
                          <div className="h-8 bg-muted rounded w-16"></div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {domainsData.map((domain) => (
                      <Card key={domain.domain} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <Link
                              to={`/domain/${domain.domain}`}
                              className="text-lg font-semibold text-foreground hover:text-primary transition-colors"
                            >
                              {domain.domain}
                            </Link>
                            {domain.bundle && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Bundle: {domain.bundle}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {domain.tags?.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="text-lg font-bold text-primary">
                                {domain.bin_price
                                  ? formatPrice(domain.bin_price)
                                  : 'Call for price'}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveDomain(domain.domain)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Add More */}
                <Card className="p-6 border-dashed">
                  <div className="text-center">
                    <Plus className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <h3 className="font-medium text-foreground mb-2">Add more domains</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Browse my collection to find more domains for your bundle
                    </p>
                    <Button asChild variant="outline">
                      <Link to="/domains">Browse Domains</Link>
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Price Panel & Form */}
              <div className="space-y-6">
                {/* Pricing Card */}
                <Card className="sticky top-8">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="w-5 h-5" />
                      Bundle Pricing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {pricing.pricedCount > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Subtotal ({pricing.pricedCount} domains)</span>
                          <span>{formatPrice(pricing.subtotal)}</span>
                        </div>

                        {pricing.discount > 0 && (
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Bundle discount ({pricing.discountLabel})</span>
                            <span>-{formatPrice(pricing.discountAmount)}</span>
                          </div>
                        )}

                        <div className="border-t pt-2">
                          <div className="flex justify-between font-semibold">
                            <span>Bundle Price</span>
                            <span className="text-primary">{formatPrice(pricing.bundlePrice)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {pricing.noPriceCount > 0 && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <AlertCircle className="w-4 h-4" />
                        <span>{pricing.noPriceCount} domains require custom pricing</span>
                      </div>
                    )}

                    {bundleItems.length >= 2 && pricing.discount === 0 && (
                      <div className="text-xs text-muted-foreground">
                        Add {Math.max(0, 2 - bundleItems.length)} more domains to unlock 5% bundle
                        discount
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Request Form */}
                <Card>
                  <CardHeader>
                    <CardTitle>Request Custom Bundle</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, email: e.target.value }))
                          }
                          required
                          placeholder="your@email.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="name">Name (optional)</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, name: e.target.value }))
                          }
                          placeholder="Your name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="intent">What will you build? *</Label>
                        <Textarea
                          id="intent"
                          value={formData.intent}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, intent: e.target.value }))
                          }
                          required
                          placeholder="Describe your project or business idea..."
                          rows={3}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full"
                        disabled={submitting || bundleItems.length === 0}
                      >
                        {submitting ? 'Submitting...' : 'Request Bundle Quote'}
                      </Button>

                      <p className="text-xs text-muted-foreground">
                        I'll review your bundle and get back to you within 24 hours with custom
                        pricing.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
